import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { Typography, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { searchParams, getParams } from "../../../utlis/helpers";
import { ACTIVITY_MONITORING } from "../../../routes/routerPath";
import PersonInfoSection from "./PersonInfoSection";
import {
  getActivityTrend,
  getSensorMapping,
} from "../../../services/activityMonitoringApi";
import { userInfoFetch, formatName } from "../../../utlis/commonFunction";
import { getCurrentLocale } from "../../../utlis/locale";
import NoDataFound from "../../common/NoDataFound";
import "./activitytrend.scss";

const formatDateForInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 6);
  return {
    from: formatDateForInput(from),
    to: formatDateForInput(to),
  };
};

const MAX_MINUTES = 1440; // 24 hours — all segments proportioned against this

const ActivityTrend = () => {
  const navigate = useNavigate();
  const [trendData, setTrendData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [sensorId, setSensorId] = useState(null);
  const chartScrollRef = useRef(null);

  const defaultRange = getDefaultDateRange();
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);

  const userInfo = userInfoFetch();
  const { seniorId: seniorIdFromParams } = getParams();
  const seniorUuid = userInfo?.id || seniorIdFromParams;

  useEffect(() => {
    let mounted = true;
    const fetchSensorId = async () => {
      if (!seniorUuid) {
        setError("Senior ID not found.");
        setIsLoading(false);
        return;
      }
      try {
        const response = await getSensorMapping(seniorUuid);
        const isSuccess = response.success || response.status === 1;
        if (mounted && isSuccess && response.data?.sensorId) {
          setSensorId(response.data.sensorId);
        } else if (mounted) {
          setError("No sensor mapped for this resident.");
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error fetching sensor mapping:", err);
        if (mounted) {
          setError("Failed to load sensor mapping.");
          setIsLoading(false);
        }
      }
    };
    fetchSensorId();
    return () => {
      mounted = false;
    };
  }, [seniorUuid]);

  const fetchTrendData = useCallback(async () => {
    if (!sensorId) return;
    setIsLoading(true);
    setError(null);
    setTooltip(null);
    try {
      const from = new Date(fromDate).getTime();
      const to = new Date(toDate + "T23:59:59").getTime();
      const response = await getActivityTrend(sensorId, from, to);
      if (response.success) {
        setTrendData(response.data);
      } else {
        setError("Failed to load trend data");
      }
    } catch (err) {
      console.error("Error fetching trend data:", err);
      setError("Failed to load trend data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [sensorId, fromDate, toDate]);

  useEffect(() => {
    if (sensorId) {
      fetchTrendData();
    }
  }, [sensorId, fetchTrendData]);

  useEffect(() => {
    if (chartScrollRef.current) {
      chartScrollRef.current.scrollLeft = chartScrollRef.current.scrollWidth;
    }
  }, [trendData]);

  const handleBack = () => {
    navigate(ACTIVITY_MONITORING + searchParams);
  };

  const handleFromDateChange = (e) => {
    const newFrom = e.target.value;
    if (newFrom > toDate) return;
    setFromDate(newFrom);
  };

  const handleToDateChange = (e) => {
    const newTo = e.target.value;
    if (newTo < fromDate) return;
    const today = formatDateForInput(new Date());
    if (newTo > today) return;
    setToDate(newTo);
  };

  const transformDataForChart = () => {
    if (!trendData?.data || trendData.data.length === 0) {
      return { dates: [], dailyData: [] };
    }

    const dates = trendData.data.map((day) => {
      const date = new Date(day.day);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    });

    // Each segment height is proportioned against 1440 min (24h)
    const dailyData = trendData.data.map((day) => ({
      bed: Math.round(((day.bed_minutes || 0) / 60) * 10) / 10,
      couch: Math.round(((day.couch_minutes || 0) / 60) * 10) / 10,
      toilet: Math.round(((day.toilet_minutes || 0) / 60) * 10) / 10,
      vacant: Math.round(((day.vacant_minutes || 0) / 60) * 10) / 10,
      // raw minutes for height %
      bed_min: day.bed_minutes || 0,
      couch_min: day.couch_minutes || 0,
      toilet_min: day.toilet_minutes || 0,
      vacant_min: day.vacant_minutes || 0,
    }));

    return { dates, dailyData };
  };

  const segmentLabels = {
    bed: "Bed",
    couch: "Couch",
    toilet: "Toilet",
    vacant: "Vacant",
  };

  const handleSegmentClick = (e, segmentType, hours, date) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      segmentType,
      hours,
      date,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const handleChartClick = () => {
    setTooltip(null);
  };

  const renderHeader = () => (
    <div className="pinned-header">
      <div className="header-left">
        <button className="back-btn" onClick={handleBack} aria-label="Back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5m0 0l7-7m-7 7l7 7"
              stroke="#000000"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <Typography className="header-title">Activity Trend</Typography>
      </div>
      <div className="header-right" />
    </div>
  );

  const renderDateSelector = () => (
    <div className="date-selector">
      <div className="date-field">
        <label className="date-label" htmlFor="from-date">
          From
        </label>
        <input
          id="from-date"
          type="date"
          className="date-input"
          value={fromDate}
          max={toDate}
          onChange={handleFromDateChange}
        />
      </div>
      <div className="date-field">
        <label className="date-label" htmlFor="to-date">
          To
        </label>
        <input
          id="to-date"
          type="date"
          className="date-input"
          value={toDate}
          min={fromDate}
          max={formatDateForInput(new Date())}
          onChange={handleToDateChange}
        />
      </div>
    </div>
  );
  /*eslint-disable*/

  const renderSummaryCards = () => {
    const s = trendData?.summary;
    if (!s) return null;

    const cards = [
      { label: "Avg Bed", value: s.averageBedHours, cls: "bed" },
      { label: "Avg Couch", value: s.averageCouchHours, cls: "couch" },
      { label: "Avg Toilet", value: s.averageToiletHours, cls: "toilet" },
      { label: "Avg Vacant", value: s.averageVacantHours, cls: "vacant" },
    ];

    return (
      <div className="summary-cards">
        {cards.map((card) => (
          <div key={card.label} className={`summary-card ${card.cls}`}>
            <span className="summary-value">
              {card.value != null ? card.value : "—"}
              <span className="summary-unit">h</span>
            </span>
            <span className="summary-label">{card.label}</span>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="activity-trend-wrapper">
        {renderHeader()}
        <PersonInfoSection
          personName={formatName(userInfo, getCurrentLocale()) || "Unknown"}
          gender={userInfo?.gender || ""}
          age={userInfo?.age || 0}
          room={userInfo?.room || ""}
          sensorId={sensorId}
        />
        {renderDateSelector()}
        <div
          className="loading-container"
          style={{ margin: "auto", marginTop: "2rem" }}
        >
          <CircularProgress size={32} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activity-trend-wrapper">
        {renderHeader()}
        <PersonInfoSection
          personName={formatName(userInfo, getCurrentLocale()) || "Unknown"}
          gender={userInfo?.gender || ""}
          age={userInfo?.age || 0}
          room={userInfo?.room || ""}
          sensorId={sensorId}
        />
        {renderDateSelector()}
        <div className="error-container">
          <Typography className="error-text">{error}</Typography>
          <button className="btn btn-primary" onClick={fetchTrendData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { dates, dailyData } = transformDataForChart();

  if (dates.length === 0) {
    return (
      <div className="activity-trend-wrapper" onClick={handleChartClick}>
        {renderHeader()}
        <PersonInfoSection
          personName={formatName(userInfo, getCurrentLocale()) || "Unknown"}
          gender={userInfo?.gender || ""}
          age={userInfo?.age || 0}
          room={userInfo?.room || ""}
          sensorId={sensorId}
        />
        {renderDateSelector()}
        <NoDataFound
          icon="activity"
          message="No activity data available"
          description="Activity data for this period will appear here once recorded."
        />
      </div>
    );
  }

  const barMinWidth = 48;
  const chartMinWidth = dates.length * barMinWidth;

  return (
    <div className="activity-trend-wrapper" onClick={handleChartClick}>
      {renderHeader()}

      <PersonInfoSection
        personName={userInfo?.name || "Unknown"}
        gender={userInfo?.gender || ""}
        age={userInfo?.age || 0}
        room={userInfo?.room || ""}
        sensorId={sensorId}
      />

      {renderDateSelector()}

      {/* {renderSummaryCards()} */}

      <div className="chart-section">
        <div className="y-axis">
          <span className="y-label">24</span>
          <span className="y-unit">(h)</span>
          <span className="y-label y-label-mid">12</span>
          <span className="y-label y-label-zero">0</span>
        </div>

        <div className="chart-scroll-container" ref={chartScrollRef}>
          <div
            className="chart-container"
            style={{ minWidth: `${chartMinWidth}px` }}
          >
            <div className="chart-area">
              <div className="grid-line grid-line-top"></div>
              <div className="grid-line grid-line-mid"></div>
              <div className="grid-line grid-line-bottom"></div>

              {/* Tooltip via portal — escapes overflow:hidden on wrapper */}
              {tooltip &&
                ReactDOM.createPortal(
                  <div
                    className="chart-tooltip-portal"
                    style={{ left: tooltip.x, top: tooltip.y }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="tooltip-label">
                      {segmentLabels[tooltip.segmentType]}
                    </span>
                    <span className="tooltip-value">{tooltip.hours} h</span>
                    <span className="tooltip-date">{tooltip.date}</span>
                  </div>,
                  document.body,
                )}

              <div className="chart-bars">
                {dailyData.map((day, index) => (
                  <div key={index} className="bar-group">
                    <div className="stacked-bar">
                      {[
                        { key: "bed", min: day.bed_min, hrs: day.bed },
                        { key: "couch", min: day.couch_min, hrs: day.couch },
                        { key: "toilet", min: day.toilet_min, hrs: day.toilet },
                        { key: "vacant", min: day.vacant_min, hrs: day.vacant },
                      ].map((seg) => (
                        <div
                          key={seg.key}
                          className={`bar-segment ${seg.key}`}
                          style={{
                            height: `${(seg.min / MAX_MINUTES) * 100}%`,
                          }}
                          onClick={(e) =>
                            handleSegmentClick(
                              e,
                              seg.key,
                              seg.hrs,
                              dates[index],
                            )
                          }
                        ></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="x-axis">
              {dates.map((date, index) => (
                <span key={index} className="x-label">
                  {date}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="chart-legend">
        <div className="legend-row">
          <div className="legend-item">
            <span className="legend-color bed"></span>
            <span>Bed</span>
          </div>
          <div className="legend-item">
            <span className="legend-color couch"></span>
            <span>Couch</span>
          </div>
          <div className="legend-item">
            <span className="legend-color toilet"></span>
            <span>Toilet</span>
          </div>
        </div>
        <div className="legend-row">
          <div className="legend-item">
            <span className="legend-color vacant"></span>
            <span>Vacant</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityTrend;
